import { ReactElement, ReactNode, useState } from 'react';
import cx from 'clsx';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Box, Icon, floatingContainerStyle } from '@ff-it/ui';
import { filterChip, filterChipActive } from './style.css';
import { filterChipBody, filterChipBodyClearable, filterChipClear, filterChipValue } from './FilterChip.css';

type FilterChipProps = {
  label: string;
  active: boolean;
  valueNode?: ReactNode;
  onClear: () => void;
  width?: number | null;
  flush?: boolean;
  onClose?: () => void;
  children: (close: () => void) => ReactNode;
};

export function FilterChip({
  label,
  active,
  valueNode,
  onClear,
  width = 260,
  flush,
  onClose,
  children,
}: FilterChipProps): ReactElement {
  const [open, setOpen] = useState(false);
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) {
      onClose?.();
    }
  };
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const close = (): void => handleOpenChange(false);

  return (
    <Box
      as="span"
      className={cx(filterChip, active && filterChipActive)}
      onKeyDown={(e: any) => {
        if (!open && active && (e.key === 'Delete' || e.key === 'Backspace')) {
          e.preventDefault();
          onClear();
        }
      }}
    >
      <Box
        as="button"
        type="button"
        ref={refs.setReference}
        className={cx(filterChipBody, active && filterChipBodyClearable)}
        {...getReferenceProps()}
      >
        {valueNode != null ? (
          <>
            {label}: <span className={filterChipValue}>{valueNode}</span>
          </>
        ) : (
          label
        )}
      </Box>
      {active && (
        <Box
          as="button"
          type="button"
          tabIndex={-1}
          aria-label={`Clear ${label}`}
          className={filterChipClear}
          onClick={() => onClear()}
        >
          <Icon icon="xmark" size="md" />
        </Box>
      )}
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className={floatingContainerStyle}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <Box style={width != null ? { width } : undefined} p={flush ? undefined : '2'}>
                {children(close)}
              </Box>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </Box>
  );
}
